import { DeleteOutlined, EditOutlined, EyeOutlined, FileWordOutlined, PaperClipOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, InputNumber, Popconfirm, Row, Space, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ContentHeader } from '../../components';
import { urlAdmin } from '../../constants';
import { Links } from '../../helpers/Links';
import { menuHelper } from '../../helpers/account';
import { KeKhaiTaiSan, TimKiemKeKhaiTaiSanRequest } from '../../models/taisan/KeKhaiTaiSan';
import { timKiemDSKeKhaiCaNhan, timKiemDSKeKhaiToanDonVi, xoaKeKhaiTaiSan, xuatBanKeKhaiTaiSan } from '../../services/keKhaiTaiSan';
import DonViSelectTree from '../utils/DonViSelectTree';
import ModalLuuKeKhaiTaiSan from './ModalLuuKeKhaiTaiSan';
import ModalXemKeKhaiTaiSan from './ModalXemKeKhaiTaiSan';

const { Text } = Typography;

const KeKhaiTaiSanPage = () => {
    const user = useSelector((state: any) => state.auth.currentUser);

    // Kiểm tra quyền theo cấu hình URL được phân quyền qua menuHelper
    const permissions: any = menuHelper.checkMultiPermission([
        Links.TaiSan.KeKhaiToanDonVi,
        Links.KeKhaiTaiSan.ToanDonVi,
    ]);
    const hasQuyenToanDonVi = !!(permissions[Links.TaiSan.KeKhaiToanDonVi] || permissions[Links.KeKhaiTaiSan.ToanDonVi] || user?.isAdmin);

    const [activeTab, setActiveTab] = useState<string>("1");

    // State cho Tab 1: Cá nhân
    const [loadingCaNhan, setLoadingCaNhan] = useState<boolean>(false);
    const [dsCaNhan, setDsCaNhan] = useState<KeKhaiTaiSan[]>([]);
    const [totalCaNhan, setTotalCaNhan] = useState<number>(0);
    const [pageCaNhan, setPageCaNhan] = useState<number>(1);
    const [pageSizeCaNhan, setPageSizeCaNhan] = useState<number>(10);
    const [filterCaNhan, setFilterCaNhan] = useState<TimKiemKeKhaiTaiSanRequest>({});

    // State cho Tab 2: Toàn đơn vị
    const [loadingToanDonVi, setLoadingToanDonVi] = useState<boolean>(false);
    const [dsToanDonVi, setDsToanDonVi] = useState<KeKhaiTaiSan[]>([]);
    const [totalToanDonVi, setTotalToanDonVi] = useState<number>(0);
    const [pageToanDonVi, setPageToanDonVi] = useState<number>(1);
    const [pageSizeToanDonVi, setPageSizeToanDonVi] = useState<number>(10);
    const [filterToanDonVi, setFilterToanDonVi] = useState<TimKiemKeKhaiTaiSanRequest>({});

    // Forms tìm kiếm
    const [formFilterCaNhan] = Form.useForm();
    const [formFilterToanDonVi] = Form.useForm();

    // Modals
    const [modalLuuOpen, setModalLuuOpen] = useState<boolean>(false);
    const [modalXemOpen, setModalXemOpen] = useState<boolean>(false);
    const [selectedIdKeKhai, setSelectedIdKeKhai] = useState<number | null>(null);
    const [loadingXuatFile, setLoadingXuatFile] = useState<boolean>(false);

    useEffect(() => {
        loadDataCaNhan(1, pageSizeCaNhan, filterCaNhan);
    }, []);

    useEffect(() => {
        if (activeTab === "2" && hasQuyenToanDonVi) {
            loadDataToanDonVi(pageToanDonVi, pageSizeToanDonVi, filterToanDonVi);
        }
    }, [activeTab, hasQuyenToanDonVi]);

    const loadDataCaNhan = async (page: number, size: number, filter: TimKiemKeKhaiTaiSanRequest) => {
        setLoadingCaNhan(true);
        try {
            const start = (page - 1) * size;
            const res = await timKiemDSKeKhaiCaNhan(filter, start, size);
            setDsCaNhan((res.data || []).map((item, idx) => ({ ...item, key: item.id || idx })));
            setTotalCaNhan(res.recordsTotal || 0);
            setPageCaNhan(page);
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi tải danh sách kê khai tài sản cá nhân");
        } finally {
            setLoadingCaNhan(false);
        }
    };

    const loadDataToanDonVi = async (page: number, size: number, filter: TimKiemKeKhaiTaiSanRequest) => {
        setLoadingToanDonVi(true);
        try {
            const start = (page - 1) * size;
            const res = await timKiemDSKeKhaiToanDonVi(filter, start, size);
            setDsToanDonVi((res.data || []).map((item, idx) => ({ ...item, key: item.id || idx })));
            setTotalToanDonVi(res.recordsTotal || 0);
            setPageToanDonVi(page);
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi tải danh sách kê khai tài sản toàn đơn vị");
        } finally {
            setLoadingToanDonVi(false);
        }
    };

    const handleSearchCaNhan = (values: any) => {
        const newFilter: TimKiemKeKhaiTaiSanRequest = {
            tuKhoa: values.tuKhoa,
            namHinhThanh: values.namHinhThanh,
        };
        setFilterCaNhan(newFilter);
        loadDataCaNhan(1, pageSizeCaNhan, newFilter);
    };

    const handleResetFilterCaNhan = () => {
        formFilterCaNhan.resetFields();
        setFilterCaNhan({});
        loadDataCaNhan(1, pageSizeCaNhan, {});
    };

    const handleSearchToanDonVi = (values: any) => {
        const newFilter: TimKiemKeKhaiTaiSanRequest = {
            tuKhoa: values.tuKhoa,
            namHinhThanh: values.namHinhThanh,
            idDonVi: values.idDonVi === 0 ? undefined : values.idDonVi,
        };
        setFilterToanDonVi(newFilter);
        loadDataToanDonVi(1, pageSizeToanDonVi, newFilter);
    };

    const handleResetFilterToanDonVi = () => {
        formFilterToanDonVi.resetFields();
        setFilterToanDonVi({});
        loadDataToanDonVi(1, pageSizeToanDonVi, {});
    };

    const handleThemMoi = () => {
        setSelectedIdKeKhai(null);
        setModalLuuOpen(true);
    };

    const handleSua = (id: number) => {
        setSelectedIdKeKhai(id);
        setModalLuuOpen(true);
    };

    const handleXem = (id: number) => {
        setSelectedIdKeKhai(id);
        setModalXemOpen(true);
    };

    const handleXoa = async (id: number) => {
        try {
            await xoaKeKhaiTaiSan(id);
            toast.success("Xóa bản kê khai thành công");
            loadDataCaNhan(pageCaNhan, pageSizeCaNhan, filterCaNhan);
            if (hasQuyenToanDonVi) {
                loadDataToanDonVi(pageToanDonVi, pageSizeToanDonVi, filterToanDonVi);
            }
        } catch (error: any) {
            toast.error(error.message || "Xóa bản kê khai thất bại");
        }
    };

    const handleXuatBanKeKhai = async (idNhanVien?: number) => {
        setLoadingXuatFile(true);
        try {
            const response = await xuatBanKeKhaiTaiSan(idNhanVien);
            let fileName = "BanKeKhaiTaiSan.docx";
            const contentDisposition = response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
            if (contentDisposition) {
                // Ưu tiên chuẩn filename*=UTF-8'' để đọc đúng tiếng Việt có dấu
                const utf8Match = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;\n]+)/i);
                if (utf8Match && utf8Match[1]) {
                    try {
                        fileName = decodeURIComponent(utf8Match[1].replace(/['"]/g, '').trim());
                    } catch (e) {
                        fileName = utf8Match[1].replace(/['"]/g, '').trim();
                    }
                } else {
                    const standardMatch = contentDisposition.match(/filename=((['"]).*?\2|[^;\n]*)/i);
                    if (standardMatch && standardMatch[1]) {
                        fileName = standardMatch[1].replace(/['"]/g, '').trim();
                    }
                }
            }

            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
            toast.success("Xuất bản kê khai tài sản thành công");
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi xuất bản kê khai tài sản");
        } finally {
            setLoadingXuatFile(false);
        }
    };

    const formatCurrency = (value?: number) => {
        if (value === undefined || value === null) return "0 đ";
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
    };

    // Columns cho Tab Cá nhân
    const columnsCaNhan: ColumnsType<KeKhaiTaiSan> = [
        {
            title: "STT",
            key: "stt",
            width: 60,
            align: 'center',
            render: (_, __, index) => (pageCaNhan - 1) * pageSizeCaNhan + index + 1,
        },
        {
            title: "Tên tài sản",
            dataIndex: "tenTaiSan",
            key: "tenTaiSan",
            render: (text, record) => (
                <a onClick={() => record.id && handleXem(record.id)} style={{ fontWeight: 500 }}>
                    {text}
                </a>
            ),
        },
        {
            title: "Năm hình thành",
            dataIndex: "namHinhThanh",
            key: "namHinhThanh",
            width: 130,
            align: 'center',
        },
        {
            title: "Nguồn gốc",
            dataIndex: "nguonGoc",
            key: "nguonGoc",
            ellipsis: true,
        },
        {
            title: "Giá trị tài sản (VNĐ)",
            dataIndex: "giaTriTaiSan",
            key: "giaTriTaiSan",
            width: 160,
            align: 'right',
            render: (val) => <Text strong style={{ color: '#2e7d32' }}>{formatCurrency(val)}</Text>,
        },
        {
            title: "Tệp đính kèm",
            key: "dsTaiLieu",
            width: 140,
            align: 'center',
            render: (_, record) => {
                const count = record.dsTaiLieu?.length || 0;
                if (count === 0) return <Text type="secondary">—</Text>;
                return (
                    <Tooltip
                        title={
                            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                {record.dsTaiLieu?.map(f => (
                                    <div key={f.id} style={{ padding: '2px 0' }}>
                                        <a
                                            href={urlAdmin + (f.duongDan || '').replace(/\\/g, '/')}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#91d5ff' }}
                                        >
                                            <PaperClipOutlined /> {f.tenTep}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        }
                    >
                        <Tag color="blue" icon={<PaperClipOutlined />} style={{ cursor: 'pointer' }} onClick={() => record.id && handleXem(record.id)}>
                            {count} tệp
                        </Tag>
                    </Tooltip>
                );
            }
        },
        {
            title: "Ghi chú",
            dataIndex: "ghiChu",
            key: "ghiChu",
            ellipsis: true,
            render: (text) => text || "—",
        },
        {
            title: "Thao tác",
            key: "action",
            width: 130,
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Xem chi tiết">
                        <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined style={{ color: '#1890ff' }} />}
                            onClick={() => record.id && handleXem(record.id)}
                        />
                    </Tooltip>
                    <Tooltip title="Chỉnh sửa">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined style={{ color: '#faad14' }} />}
                            onClick={() => record.id && handleSua(record.id)}
                        />
                    </Tooltip>
                    <Tooltip title="Xóa">
                        <Popconfirm
                            title="Bạn có chắc chắn muốn xóa bản kê khai này?"
                            onConfirm={() => record.id && handleXoa(record.id)}
                            okText="Xóa"
                            cancelText="Hủy"
                            okButtonProps={{ danger: true }}
                        >
                            <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        }
    ];

    // Columns cho Tab Toàn đơn vị (Chỉ xem)
    const columnsToanDonVi: ColumnsType<KeKhaiTaiSan> = [
        {
            title: "STT",
            key: "stt",
            width: 50,
            align: 'center',
            render: (_, __, index) => (pageToanDonVi - 1) * pageSizeToanDonVi + index + 1,
        },
        {
            title: "Người kê khai",
            key: "tenNhanVien",
            width: 170,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{record.tenNhanVien || "—"}</div>
                    {record.maNhanVien && <Text type="secondary" style={{ fontSize: 12 }}>{record.maNhanVien}</Text>}
                </div>
            )
        },
        {
            title: "Phòng ban / Đơn vị",
            dataIndex: "tenDonVi",
            key: "tenDonVi",
            width: 160,
            render: (text) => text || "—",
        },
        {
            title: "Tên tài sản",
            dataIndex: "tenTaiSan",
            key: "tenTaiSan",
            render: (text, record) => (
                <a onClick={() => record.id && handleXem(record.id)} style={{ fontWeight: 500 }}>
                    {text}
                </a>
            ),
        },
        {
            title: "Năm hình thành",
            dataIndex: "namHinhThanh",
            key: "namHinhThanh",
            width: 120,
            align: 'center',
        },
        {
            title: "Nguồn gốc",
            dataIndex: "nguonGoc",
            key: "nguonGoc",
            ellipsis: true,
        },
        {
            title: "Giá trị tài sản (VNĐ)",
            dataIndex: "giaTriTaiSan",
            key: "giaTriTaiSan",
            width: 150,
            align: 'right',
            render: (val) => <Text strong style={{ color: '#2e7d32' }}>{formatCurrency(val)}</Text>,
        },
        {
            title: "Tệp đính kèm",
            key: "dsTaiLieu",
            width: 120,
            align: 'center',
            render: (_, record) => {
                const count = record.dsTaiLieu?.length || 0;
                if (count === 0) return <Text type="secondary">—</Text>;
                return (
                    <Tooltip
                        title={
                            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                {record.dsTaiLieu?.map(f => (
                                    <div key={f.id} style={{ padding: '2px 0' }}>
                                        <a
                                            href={urlAdmin + (f.duongDan || '').replace(/\\/g, '/')}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#91d5ff' }}
                                        >
                                            <PaperClipOutlined /> {f.tenTep}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        }
                    >
                        <Tag color="blue" icon={<PaperClipOutlined />} style={{ cursor: 'pointer' }} onClick={() => record.id && handleXem(record.id)}>
                            {count} tệp
                        </Tag>
                    </Tooltip>
                );
            }
        },
        {
            title: "Ghi chú",
            dataIndex: "ghiChu",
            key: "ghiChu",
            ellipsis: true,
            render: (text) => text || "—",
        },
        {
            title: "Thao tác",
            key: "action",
            width: 100,
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Xem chi tiết">
                        <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined style={{ color: '#1890ff' }} />}
                            onClick={() => record.id && handleXem(record.id)}
                        />
                    </Tooltip>
                    <Tooltip title="Xuất bản kê khai">
                        <Button
                            type="text"
                            size="small"
                            icon={<FileWordOutlined style={{ color: '#52c41a' }} />}
                            onClick={() => record.idNhanVien && handleXuatBanKeKhai(record.idNhanVien)}
                        />
                    </Tooltip>
                </Space>
            ),
        }
    ];

    return (
        <div>
            <ContentHeader
                title="Cá nhân kê khai tài sản"
                subTitle="Quản lý thông tin kê khai tài sản cá nhân và toàn đơn vị"
            />

            <div className="content">
                <div className="container-fluid">
                    <Card style={{ marginBottom: 20 }}>
                        <Tabs
                            activeKey={activeTab}
                            onChange={(key) => setActiveTab(key)}
                            items={[
                                {
                                    key: "1",
                                    label: "Kê khai tài sản cá nhân",
                                    children: (
                                        <div>
                                            {/* Bộ lọc tìm kiếm Tab Cá nhân */}
                                            <Form
                                                form={formFilterCaNhan}
                                                onFinish={handleSearchCaNhan}
                                                layout="vertical"
                                                style={{ marginBottom: 16 }}
                                            >
                                                <Row gutter={16} align="bottom">
                                                    <Col xs={24} sm={12} md={8} lg={8}>
                                                        <Form.Item name="tuKhoa" label="Từ khóa tìm kiếm" style={{ marginBottom: 8 }}>
                                                            <Input placeholder="Tên tài sản, nguồn gốc..." allowClear />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} sm={12} md={6} lg={6}>
                                                        <Form.Item name="namHinhThanh" label="Năm hình thành" style={{ marginBottom: 8 }}>
                                                            <InputNumber
                                                                style={{ width: '100%' }}
                                                                min={1900}
                                                                max={2100}
                                                                placeholder="VD: 2023"
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} sm={24} md={12} lg={12} style={{ marginBottom: 8 }}>
                                                        <Space wrap>
                                                            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                                                                Tìm kiếm
                                                            </Button>
                                                            <Button icon={<ReloadOutlined />} onClick={handleResetFilterCaNhan}>
                                                                Làm mới
                                                            </Button>
                                                            <Button type="primary" icon={<PlusOutlined />} onClick={handleThemMoi} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                                                                Thêm kê khai tài sản
                                                            </Button>
                                                            <Button
                                                                icon={<FileWordOutlined />}
                                                                onClick={() => handleXuatBanKeKhai()}
                                                                loading={loadingXuatFile}
                                                                style={{ color: '#1890ff', borderColor: '#1890ff' }}
                                                            >
                                                                Xuất bản kê khai
                                                            </Button>
                                                        </Space>
                                                    </Col>
                                                </Row>
                                            </Form>

                                            {/* Bảng danh sách Tab Cá nhân */}
                                            <Table
                                                columns={columnsCaNhan}
                                                dataSource={dsCaNhan}
                                                loading={loadingCaNhan}
                                                rowKey="id"
                                                pagination={{
                                                    current: pageCaNhan,
                                                    pageSize: pageSizeCaNhan,
                                                    total: totalCaNhan,
                                                    showSizeChanger: true,
                                                    pageSizeOptions: ['10', '20', '50'],
                                                    showTotal: (total) => `Tổng cộng ${total} bản ghi`,
                                                    onChange: (page, size) => {
                                                        setPageSizeCaNhan(size);
                                                        loadDataCaNhan(page, size, filterCaNhan);
                                                    }
                                                }}
                                                bordered
                                                size="middle"
                                                scroll={{ x: 1000 }}
                                            />
                                        </div>
                                    )
                                },
                                // Chỉ render Tab 2 nếu người dùng có quyền URL (Lãnh đạo / Quyền toàn đơn vị)
                                ...(hasQuyenToanDonVi ? [
                                    {
                                        key: "2",
                                        label: "Kê khai tài sản toàn đơn vị",
                                        children: (
                                            <div>
                                                {/* Bộ lọc tìm kiếm Tab Toàn đơn vị */}
                                                <Form
                                                    form={formFilterToanDonVi}
                                                    onFinish={handleSearchToanDonVi}
                                                    layout="vertical"
                                                    style={{ marginBottom: 16 }}
                                                >
                                                    <Row gutter={16} align="bottom">
                                                        <Col xs={24} sm={12} md={8} lg={6}>
                                                            <Form.Item name="tuKhoa" label="Từ khóa" style={{ marginBottom: 8 }}>
                                                                <Input placeholder="Tên tài sản, người kê khai, mã NV..." allowClear />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={12} md={8} lg={6}>
                                                            <Form.Item name="idDonVi" label="Phòng ban / Đơn vị" style={{ marginBottom: 8 }}>
                                                                <DonViSelectTree
                                                                    optionAll
                                                                    autoSelectFirst={false}
                                                                    allowClear
                                                                    onChange={(e: any) => {
                                                                        formFilterToanDonVi.setFieldsValue({ idDonVi: e?.target?.value });
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={12} md={4} lg={4}>
                                                            <Form.Item name="namHinhThanh" label="Năm hình thành" style={{ marginBottom: 8 }}>
                                                                <InputNumber
                                                                    style={{ width: '100%' }}
                                                                    min={1900}
                                                                    max={2100}
                                                                    placeholder="VD: 2023"
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={24} md={8} lg={8} style={{ marginBottom: 8 }}>
                                                            <Space wrap>
                                                                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                                                                    Tìm kiếm
                                                                </Button>
                                                                <Button icon={<ReloadOutlined />} onClick={handleResetFilterToanDonVi}>
                                                                    Làm mới
                                                                </Button>
                                                            </Space>
                                                        </Col>
                                                    </Row>
                                                </Form>

                                                {/* Bảng danh sách Tab Toàn đơn vị (Chỉ xem) */}
                                                <Table
                                                    columns={columnsToanDonVi}
                                                    dataSource={dsToanDonVi}
                                                    loading={loadingToanDonVi}
                                                    rowKey="id"
                                                    pagination={{
                                                        current: pageToanDonVi,
                                                        pageSize: pageSizeToanDonVi,
                                                        total: totalToanDonVi,
                                                        showSizeChanger: true,
                                                        pageSizeOptions: ['10', '20', '50'],
                                                        showTotal: (total) => `Tổng cộng ${total} bản ghi`,
                                                        onChange: (page, size) => {
                                                            setPageSizeToanDonVi(size);
                                                            loadDataToanDonVi(page, size, filterToanDonVi);
                                                        }
                                                    }}
                                                    bordered
                                                    size="middle"
                                                    scroll={{ x: 1200 }}
                                                />
                                            </div>
                                        )
                                    }
                                ] : [])
                            ]}
                        />
                    </Card>
                </div>
            </div>

            {/* Modal Thêm mới / Chỉnh sửa */}
            <ModalLuuKeKhaiTaiSan
                open={modalLuuOpen}
                idKeKhai={selectedIdKeKhai}
                onClose={() => setModalLuuOpen(false)}
                onSuccess={() => {
                    loadDataCaNhan(pageCaNhan, pageSizeCaNhan, filterCaNhan);
                    if (hasQuyenToanDonVi) {
                        loadDataToanDonVi(pageToanDonVi, pageSizeToanDonVi, filterToanDonVi);
                    }
                }}
            />

            {/* Modal Xem chi tiết */}
            <ModalXemKeKhaiTaiSan
                open={modalXemOpen}
                idKeKhai={selectedIdKeKhai}
                onClose={() => setModalXemOpen(false)}
            />
        </div>
    );
};

export default KeKhaiTaiSanPage;
